<template>
  <!-- ========== MOBILE OVERLAY ========== -->
  <Transition
    enter-active-class="transition-opacity duration-300 ease-out"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-200 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="isMobileOpen"
      class="sidebar-overlay"
      @click="closeMobile"
    />
  </Transition>

  <!-- ========== SIDEBAR ========== -->
  <aside
    class="sidebar"
    :class="{
      'sidebar--mobile-open': isMobileOpen,
      'sidebar--collapsed': isCollapsed && !isMobile
    }"
  >
    <!-- Logo -->
    <div class="sidebar-logo">
      <img src="@/assets/logo.png" alt="Logo" class="brand-logo-img" />
      <Transition
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="opacity-0 -translate-x-2"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0 -translate-x-2"
      >
        <div v-if="!isCollapsed || isMobile" class="sidebar-logo-text-wrapper">
          <span class="sidebar-logo-text">
            {{ appTitle }}
          </span>
        </div>
      </Transition>
    </div>

    <!-- Navigation -->
    <nav class="sidebar-nav">
      <router-link
        v-for="item in navigation"
        :key="item.to"
        :to="item.to"
        class="sidebar-nav-item"
        :class="{ 'sidebar-nav-item--active': isActive(item.to) }"
        :title="(isCollapsed && !isMobile) ? item.name : undefined"
        @click="closeMobile"
      >
        <div class="sidebar-nav-icon">
          <component :is="item.icon" class="h-5 w-5" />
        </div>
        <span class="sidebar-nav-label">{{ item.name }}</span>

        <!-- Active indicator -->
        <div v-if="isActive(item.to)" class="sidebar-nav-indicator" />
      </router-link>
    </nav>

    <!-- Collapse toggle (desktop only) -->
    <button
      v-if="!isMobile"
      class="sidebar-collapse-btn"
      @click="isCollapsed = !isCollapsed"
      :title="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
    >
      <ChevronLeft v-if="!isCollapsed" class="h-4 w-4" />
      <ChevronRight v-else class="h-4 w-4" />
      <span v-if="!isCollapsed" class="text-xs ml-2">Collapse</span>
    </button>

    <!-- User section -->
    <div class="sidebar-user">
      <div class="sidebar-user-avatar">
        {{ userInitials }}
      </div>
      <div v-if="!isCollapsed || isMobile" class="sidebar-user-info">
        <p class="sidebar-user-name">{{ currentUser?.name || 'User' }}</p>
        <p class="sidebar-user-email">{{ currentUser?.email || 'user@example.com' }}</p>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useUser } from '@/composables/auth/useUser'
import { routes, getNavigationItems } from '@/router'
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'

const props = defineProps<{
  isOpen: boolean
  collapsed: boolean
}>()

const emit = defineEmits<{
  'update:isOpen': [value: boolean]
  'update:collapsed': [value: boolean]
}>()

const route = useRoute()
const { userInitials, currentUser } = useUser()

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'Diasporiques ABA Solutions LLC')
const navigation = computed(() => getNavigationItems(routes))

// Mobile detection
const isMobile = ref(false)
const checkMobile = () => {
  isMobile.value = window.innerWidth < 1024
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

// Collapsed state (desktop only) - synced with parent
const isCollapsed = computed({
  get: () => props.collapsed,
  set: (val) => emit('update:collapsed', val)
})

// Mobile open state (synced with parent)
const isMobileOpen = computed({
  get: () => props.isOpen,
  set: (val) => emit('update:isOpen', val)
})

function closeMobile() {
  if (isMobile.value) {
    emit('update:isOpen', false)
  }
}

function isActive(path: string) {
  return route.path === path
}
</script>

<style scoped>
/* ============================================
   OVERLAY (MOBILE)
   ============================================ */

.sidebar-overlay {
  @apply fixed inset-0 z-40
         bg-black/50 dark:bg-black/70
         backdrop-blur-sm
         lg:hidden;
}

/* ============================================
   SIDEBAR
   ============================================ */

.sidebar {
  @apply fixed inset-y-0 left-0 z-50
         flex flex-col
         w-64
          bg-gradient-to-b from-white to-gray-50
         dark:from-gray-800 dark:to-gray-900
         border-r border-primary/20 dark:border-primary-dark/20
         shadow-xl dark:shadow-2xl
         transition-all duration-300 ease-in-out;
}

/* Mobile: hidden by default, slides in */
.sidebar {
  transform: translateX(-100%);
}

.sidebar--mobile-open {
  transform: translateX(0);
}

/* Desktop: always visible */
@media (min-width: 1024px) {
  .sidebar {
    transform: translateX(0);
  }

  .sidebar--collapsed {
    @apply w-[4.5rem];
  }
}

/* ============================================
   LOGO
   ============================================ */

.sidebar-logo {
  @apply flex items-center gap-3
         h-16 px-4
         border-b border-gray-200 dark:border-gray-700
         overflow-hidden;
}

.brand-logo-img {
  @apply h-10 w-10 flex-shrink-0 rounded-xl object-contain;
}

.sidebar-logo-text-wrapper {
  @apply flex-1 min-w-0 overflow-hidden;
}

.sidebar-logo-text {
  @apply text-base font-bold
         text-primary dark:text-primary-dark
         leading-tight
         block
         overflow-hidden text-ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
}

/* ============================================
   NAVIGATION
   ============================================ */

.sidebar-nav {
  @apply flex-1 px-3 py-4 space-y-1 overflow-y-auto overflow-x-hidden;
}

.sidebar-nav-item {
  @apply relative flex items-center gap-3
         px-3 py-2.5
         rounded-xl
         text-gray-600 dark:text-gray-400
         font-medium text-sm
         transition-all duration-200 ease-in-out
         overflow-hidden
         whitespace-nowrap;
}

.sidebar-nav-item:hover {
  @apply bg-gray-100 dark:bg-gray-700/30
         text-gray-900 dark:text-gray-200
         text-gray-900 dark:text-gray-200;
}

.sidebar-nav-item--active {
  @apply bg-primary/10 dark:bg-primary-dark/20
         text-primary dark:text-primary-dark
         font-semibold
         border-l-2 border-primary dark:border-primary-dark;
}

.sidebar-nav-icon {
  @apply flex-shrink-0 w-8 h-8
         flex items-center justify-center
         rounded-lg
         transition-all duration-200;
}

.sidebar-nav-item--active .sidebar-nav-icon {
  @apply bg-primary/15 dark:bg-primary-dark/25;
}

.sidebar-nav-label {
  @apply transition-opacity duration-200;
}

/* Collapsed: hide label */
@media (min-width: 1024px) {
  .sidebar--collapsed .sidebar-nav-label {
    @apply hidden;
  }

  .sidebar--collapsed .sidebar-nav-item {
    @apply justify-center px-0;
  }

  .sidebar--collapsed .sidebar-logo {
    @apply justify-center px-2;
  }
}

/* Active indicator bar */
.sidebar-nav-indicator {
  @apply absolute right-0 top-1/2 -translate-y-1/2
         w-1 h-6
         bg-gradient-to-b from-primary to-primary-hover
         dark:from-primary-dark dark:to-primary-dark
         rounded-l-full;
}

/* ============================================
   COLLAPSE TOGGLE
   ============================================ */

.sidebar-collapse-btn {
  @apply flex items-center gap-2
         mx-3 mb-2 px-3 py-2
         rounded-xl
         text-xs text-primary dark:text-primary-dark
         hover:text-primary dark:hover:text-primary-dark
         hover:bg-gray-100 dark:hover:bg-gray-700/50
         transition-all duration-200
         whitespace-nowrap;
}

/* ============================================
   USER SECTION
   ============================================ */

.sidebar-user {
  @apply flex items-center gap-3
         p-4
         border-t border-primary/20 dark:border-primary-dark/20
         overflow-hidden;
}

.sidebar-user-avatar {
  @apply h-9 w-9 flex-shrink-0
         flex items-center justify-center
         rounded-full
         bg-gradient-to-br from-primary to-primary-hover
         dark:from-primary-dark dark:to-primary-dark
         text-white text-xs font-bold
         shadow-md shadow-primary/20 dark:shadow-primary-dark/20;
}

.sidebar-user-info {
  @apply min-w-0 flex-1
         transition-opacity duration-200;
}

.sidebar-user-name {
  @apply text-sm font-semibold
         text-gray-900 dark:text-white
         truncate;
}

.sidebar-user-email {
  @apply text-xs text-gray-500 dark:text-gray-400
         truncate;
}

/* Collapsed: hide user info, center avatar */
@media (min-width: 1024px) {
  .sidebar--collapsed .sidebar-user {
    @apply justify-center;
  }
}
</style>
